import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Paper,
  Fab,
  Tabs,
  Tab,
  Badge,
  Tooltip,
  Pagination,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Link as MuiLink,
  useTheme,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  CalendarMonth,
  Person,
  RoomService,
  EventAvailable,
  Schedule,
  Close,
  CheckCircle,
  Cancel,
  Warning,
  Undo,
  Refresh,
} from '@mui/icons-material';
import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { motion } from 'framer-motion';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ConfirmationModal, AlertModal } from '../components/common/ConfirmationModal';
import { CustomSnackbar } from '../components/common/CustomSnackbar';
import { useConfirmation } from '../hooks/useConfirmation';
import AvailabilityDatePicker from '../components/AvailabilityDatePicker';
import TimeSlotPicker from '../components/TimeSlotPicker';
import {
  GET_ALL_APPOINTMENTS,
  GET_ALL_STAFF,
  GET_ACTIVE_SERVICES,
  GET_PASSED_APPOINTMENTS,
  GET_APPOINTMENTS_COUNT,
} from '../graphql/queries';
import {
  CREATE_APPOINTMENT,
  UPDATE_APPOINTMENT,
  DELETE_APPOINTMENT,
  ISSUE_REFUND,
  SYNC_PAYMENT_STATUS,
} from '../graphql/mutations';
import { categorizeAppointmentGroups, groupAppointments, getStatusColor, getStatusLabel, getAppointmentStatus, AppointmentGroup } from '../utils/appointmentUtils';
import { formatDateWithDay, formatTime, formatTimeRange, convertUTCToDateTimeLocal, convertDateTimeLocalToUTC, getStoreTimezone } from '../utils/timezone';
import { formatGBP } from '../utils/currency';
import LoadingSpinner from '../components/common/LoadingSpinner';

interface AppointmentFormData {
  staffId: string;
  serviceId: string;
  scheduledDate: string;
  selectedTime: string;
  clientName: string;
  clientPhone: string;
  clientEmail: string;
  clientComment: string;
}

const Appointments = () => {
  const theme = useTheme();
  const { storeId: paramStoreId } = useParams();
  const { storeId: authStoreId } = useAuth();
  const storeId = paramStoreId || authStoreId;

  // Custom confirmation hook
  const {
    confirmationState,
    alertState,
    snackbarState,
    confirmDelete,
    showValidationAlert,
    closeConfirmation,
    closeAlert,
    handleConfirm,
    showErrorSnackbar,
    showSuccessSnackbar,
    closeSnackbar,
    handleMutationResponse,
  } = useConfirmation();

  // Update localStorage when storeId from URL changes
  useEffect(() => {
    if (paramStoreId) {
      localStorage.setItem('storeId', paramStoreId);
    }
  }, [paramStoreId]);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<any>(null);
  const [selectedServiceIndex, setSelectedServiceIndex] = useState<number>(0);
  const [currentTab, setCurrentTab] = useState(0);
  const [formData, setFormData] = useState<AppointmentFormData>({
    staffId: '',
    serviceId: '',
    scheduledDate: '',
    selectedTime: '',
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    clientComment: '',
  });

  // Status update modal state
  const [statusUpdateModal, setStatusUpdateModal] = useState<{
    open: boolean;
    group: AppointmentGroup | null;
  }>({
    open: false,
    group: null,
  });

  // ─── Passed-tab filters & pagination ────────────────────────────────────
  // The Passed tab fetches a paginated/filtered slice from the server
  // (instead of slicing the full client-side categorisation), so the page
  // stays fast as the appointment table grows. Filters are URL-synced via
  // useSearchParams so reloads / shared links preserve state.
  const PASSED_PAGE_SIZE = 20;
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const ninetyDaysAgoISO = () => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().slice(0, 10);
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const [passedFilters, setPassedFiltersState] = useState({
    startDate: searchParams.get('from') || ninetyDaysAgoISO(),
    endDate: searchParams.get('to') || todayISO(),
    status: (searchParams.get('status') || 'all') as 'all' | 'show' | 'no_show',
    staffId: searchParams.get('staff') || 'all',
    page: Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1),
  });

  // Wrapper that updates state AND URL params together. Resets page to 1
  // whenever a filter (other than page itself) changes, so the user doesn't
  // get stranded on page 5 of a result set that no longer has 5 pages.
  const setPassedFilters = (
    next: Partial<typeof passedFilters>,
    opts?: { resetPage?: boolean }
  ) => {
    setPassedFiltersState(prev => {
      const merged = { ...prev, ...next };
      if (opts?.resetPage) merged.page = 1;
      const params = new URLSearchParams(searchParams);
      params.set('from', merged.startDate);
      params.set('to', merged.endDate);
      params.set('status', merged.status);
      params.set('staff', merged.staffId);
      params.set('page', String(merged.page));
      setSearchParams(params, { replace: true });
      return merged;
    });
  };

  // Variables shared between the passed-list query and the count query.
  // statusIn defaults to ['show', 'no_show'] (everything that's truly passed)
  // when the user picks "All", so we never accidentally pull scheduled
  // appointments that should be living on the Upcoming/NeedsAction tabs.
  const passedQueryVars = useMemo(() => ({
    storeId,
    startDate: passedFilters.startDate || undefined,
    endDate: passedFilters.endDate || undefined,
    staffId: passedFilters.staffId !== 'all' ? passedFilters.staffId : undefined,
    statusIn: passedFilters.status === 'all'
      ? ['show', 'no_show']
      : [passedFilters.status],
  }), [storeId, passedFilters.startDate, passedFilters.endDate, passedFilters.staffId, passedFilters.status]);

  const { data: appointmentsData, loading: appointmentsLoading, refetch } = useQuery(
    GET_ALL_APPOINTMENTS,
    {
      variables: { storeId },
      skip: !storeId,
      fetchPolicy: 'network-only',
    }
  );

  // Paginated passed appointments — only fetched when the Passed tab is active
  // to avoid a wasted round-trip on initial load. The 'network-only' fetch
  // policy means returning to the Passed tab after a mutation elsewhere (e.g.
  // marking a Needs-Action appointment as show) automatically refetches, so
  // we don't need to manually refetch on every mutation.
  const {
    data: passedData,
    loading: passedLoading,
  } = useQuery(GET_PASSED_APPOINTMENTS, {
    variables: {
      ...passedQueryVars,
      limit: PASSED_PAGE_SIZE,
      offset: (passedFilters.page - 1) * PASSED_PAGE_SIZE,
      orderBy: '-scheduled_date',
    },
    skip: !storeId || currentTab !== 2,
    fetchPolicy: 'network-only',
  });

  const { data: passedCountData } = useQuery(GET_APPOINTMENTS_COUNT, {
    variables: passedQueryVars,
    skip: !storeId || currentTab !== 2,
    fetchPolicy: 'network-only',
  });

  const { data: staffData } = useQuery(GET_ALL_STAFF, {
    variables: { storeId },
    skip: !storeId,
  });

  const { data: servicesData } = useQuery(GET_ACTIVE_SERVICES, {
    variables: { storeId },
    skip: !storeId,
  });

  const [createAppointment] = useMutation(CREATE_APPOINTMENT);
  const [updateAppointment] = useMutation(UPDATE_APPOINTMENT);
  const [deleteAppointment] = useMutation(DELETE_APPOINTMENT);
  const [issueRefund] = useMutation(ISSUE_REFUND);
  const [syncPaymentStatus] = useMutation(SYNC_PAYMENT_STATUS);

  const appointments = appointmentsData?.allAppointments || [];
  const staff = staffData?.allStaff || [];
  const services = servicesData?.activeServices || [];

  // Categorize appointments using the new grouping system. The `passed`
  // bucket is intentionally dropped — the Passed tab now uses server-side
  // pagination (passedGroups below) instead of client-side categorization.
  const categorizedAppointments = categorizeAppointmentGroups(appointments);
  const { upcoming, needsAction } = categorizedAppointments;

  // Server-paginated Passed tab data, grouped the same way the other tabs are.
  const passedAppointmentsRaw = passedData?.allAppointments || [];
  const passedGroups = useMemo(
    () => groupAppointments(passedAppointmentsRaw).sort((a, b) => {
      // Match the existing Passed sort order: latest appointment first.
      return new Date(b.latestTime).getTime() - new Date(a.latestTime).getTime();
    }),
    [passedAppointmentsRaw]
  );
  const passedTotalCount: number = passedCountData?.appointmentsCount ?? 0;
  const passedTotalPages = Math.max(1, Math.ceil(passedTotalCount / PASSED_PAGE_SIZE));

  // Filter services based on selected staff
  const filteredServices = formData.staffId
    ? services.filter((service: any) => 
        service.staffs?.some((staffMember: any) => staffMember.id === formData.staffId)
      )
    : services;

  // Filter staff based on selected service
  const filteredStaff = formData.serviceId
    ? staff.filter((staffMember: any) => 
        staffMember.services?.some((service: any) => service.id === formData.serviceId)
      )
    : staff;

  // Helper function to extract date and time from datetime string
  const extractDateAndTime = (dateTimeString: string) => {
    if (!dateTimeString) return { date: '', time: '' };
    const dateTime = new Date(dateTimeString);
    const date = dateTime.toISOString().split('T')[0];
    const time = dateTime.toTimeString().slice(0, 5);
    return { date, time };
  };

  const handleOpenDialog = (appointmentOrGroup?: any) => {
    const timezone = getStoreTimezone();
    
    if (appointmentOrGroup) {
      // Check if it's a single appointment or a group
      const isGroup = appointmentOrGroup.appointments && appointmentOrGroup.appointments.length > 0;
      
      if (isGroup) {
        // Editing a group - use the first appointment's data as defaults
        const firstAppointment = appointmentOrGroup.appointments[0];
        const dateTimeLocal = convertUTCToDateTimeLocal(firstAppointment.scheduledDate, timezone);
        const { date, time } = extractDateAndTime(dateTimeLocal);
        
        setEditingAppointment(appointmentOrGroup);
        setSelectedServiceIndex(0); // Reset to first service
        setFormData({
          staffId: firstAppointment.staff?.id || '',
          serviceId: firstAppointment.service?.id || '', // Pre-select first service for editing
          scheduledDate: date,
          selectedTime: time,
          clientName: appointmentOrGroup.clientName || '',
          clientPhone: appointmentOrGroup.clientPhone || '',
          clientEmail: appointmentOrGroup.clientEmail || '',
          clientComment: firstAppointment.clientComment || '',
        });
      } else {
        // Editing a single appointment
        const dateTimeLocal = convertUTCToDateTimeLocal(appointmentOrGroup.scheduledDate, timezone);
        const { date, time } = extractDateAndTime(dateTimeLocal);
        
        setEditingAppointment(appointmentOrGroup);
        setSelectedServiceIndex(0);
        setFormData({
          staffId: appointmentOrGroup.staff?.id || '',
          serviceId: appointmentOrGroup.service?.id || '',
          scheduledDate: date,
          selectedTime: time,
          clientName: appointmentOrGroup.client?.name || '',
          clientPhone: appointmentOrGroup.client?.phone || '',
          clientEmail: appointmentOrGroup.client?.email || '',
          clientComment: appointmentOrGroup.clientComment || '',
        });
      }
    } else {
      setEditingAppointment(null);
      setSelectedServiceIndex(0);
      setFormData({
        staffId: '',
        serviceId: '',
        scheduledDate: '',
        selectedTime: '',
        clientName: '',
        clientPhone: '',
        clientEmail: '',
        clientComment: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAppointment(null);
  };

  const handleClearStaff = () => {
    setFormData({ ...formData, staffId: '' });
  };

  const handleClearService = () => {
    setFormData({ ...formData, serviceId: '' });
  };

  const handleStaffChange = (staffId: string) => {
    setFormData({ 
      ...formData, 
      staffId,
      // Clear service if it's not available for the selected staff
      serviceId: staffId && formData.serviceId ? 
        (services.some((service: any) => 
          service.id === formData.serviceId && 
          service.staffs?.some((staffMember: any) => staffMember.id === staffId)
        ) ? formData.serviceId : '') : formData.serviceId
    });
  };

  const handleServiceChange = (serviceId: string) => {
    setFormData({ 
      ...formData, 
      serviceId,
      // Clear staff if they can't provide the selected service
      staffId: serviceId && formData.staffId ? 
        (staff.some((staffMember: any) => 
          staffMember.id === formData.staffId && 
          staffMember.services?.some((service: any) => service.id === serviceId)
        ) ? formData.staffId : '') : formData.staffId
    });
  };

  const handleServiceSelect = (index: number) => {
    if (editingAppointment && editingAppointment.appointments) {
      const selectedAppointment = editingAppointment.appointments[index];
      const timezone = getStoreTimezone();
      const dateTimeLocal = convertUTCToDateTimeLocal(selectedAppointment.scheduledDate, timezone);
      const { date, time } = extractDateAndTime(dateTimeLocal);
      
      setSelectedServiceIndex(index);
      setFormData({
        ...formData,
        serviceId: selectedAppointment.service?.id || '',
        scheduledDate: date,
        selectedTime: time,
        staffId: selectedAppointment.staff?.id || '',
        clientComment: selectedAppointment.clientComment || '',
      });
    }
  };

  const validateForm = () => {
    const errors = [];
    
    if (!formData.clientName.trim()) {
      errors.push('Client name is required');
    }
    if (!formData.clientPhone.trim()) {
      errors.push('Client phone is required');
    }
    if (!formData.clientEmail.trim()) {
      errors.push('Client email is required');
    }
    if (!formData.scheduledDate) {
      errors.push('Schedule date is required');
    }
    if (!formData.selectedTime) {
      errors.push('Schedule time is required');
    }
    if (!formData.staffId && filteredStaff.length === 0) {
      errors.push('At least one staff member must be available');
    }
    if (!formData.staffId && filteredStaff.length > 0) {
      errors.push('Please select a staff member');
    }
    if (!formData.serviceId && filteredServices.length === 0) {
      errors.push('At least one service must be available');
    }
    if (!formData.serviceId && filteredServices.length > 0) {
      errors.push('Please select a service');
    }
    
    return errors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      showValidationAlert(validationErrors);
      return;
    }

    // Combine date and time into a full datetime string
    const combinedDateTime = `${formData.scheduledDate}T${formData.selectedTime}:00`;
    const timezone = getStoreTimezone();
    const utcScheduledDate = convertDateTimeLocalToUTC(combinedDateTime, timezone);

    try {
      if (editingAppointment) {
        // Check if we're editing a group or single appointment
        const isGroup = editingAppointment.appointments && editingAppointment.appointments.length > 0;
        
        if (isGroup) {
          // Update only the selected appointment in the group
          const selectedAppointment = editingAppointment.appointments[selectedServiceIndex];
          
          const result = await updateAppointment({
            variables: {
              appointmentId: selectedAppointment.id,
              storeId,
              staffId: formData.staffId,
              serviceId: formData.serviceId,
              scheduledDate: utcScheduledDate,
              clientName: formData.clientName,
              clientPhone: formData.clientPhone,
              clientEmail: formData.clientEmail,
              clientComment: formData.clientComment,
            },
          });

          if (handleMutationResponse(result, 'Service updated successfully!', 'updateAppointment')) {
            // Update the appointment in the editing state to reflect changes
            const updatedAppointments = [...editingAppointment.appointments];
            updatedAppointments[selectedServiceIndex] = {
              ...selectedAppointment,
              staff: staff.find((s: any) => s.id === formData.staffId),
              service: services.find((s: any) => s.id === formData.serviceId),
              scheduledDate: utcScheduledDate,
              client: {
                ...selectedAppointment.client,
                name: formData.clientName,
                phone: formData.clientPhone,
                email: formData.clientEmail,
                comment: formData.clientComment,
              }
            };
            setEditingAppointment({
              ...editingAppointment,
              appointments: updatedAppointments
            });
            refetch();
          }
        } else {
          // Update single appointment
          const result = await updateAppointment({
            variables: {
              appointmentId: editingAppointment.id,
              storeId,
              ...formData,
              scheduledDate: utcScheduledDate,
            },
          });
          
          if (handleMutationResponse(result, 'Appointment updated successfully!', 'updateAppointment')) {
            refetch();
            handleCloseDialog();
          }
        }
      } else {
        const result = await createAppointment({
          variables: {
            storeId,
            staffId: formData.staffId,
            serviceId: formData.serviceId,
            scheduledDate: utcScheduledDate,
            clientName: formData.clientName,
            clientPhone: formData.clientPhone,
            clientEmail: formData.clientEmail,
            clientComment: formData.clientComment,
          },
        });
        
        if (handleMutationResponse(result, 'Appointment created successfully!', 'createAppointment')) {
          refetch();
          handleCloseDialog();
        }
      }
    } catch (error) {
      console.error('Error saving appointment:', error);
      showErrorSnackbar('Failed to save appointment. Please try again.');
    }
  };

  const handleDeleteGroup = async (group: AppointmentGroup) => {
    const label = group.appointments.length === 1
      ? 'this appointment'
      : `this group of ${group.appointments.length} appointments`;
    confirmDelete(label, async () => {
      try {
        for (const appointment of group.appointments) {
          await deleteAppointment({ variables: { appointmentId: appointment.id } });
        }
        showSuccessSnackbar(
          group.appointments.length === 1
            ? 'Appointment deleted successfully!'
            : 'All appointments deleted successfully!'
        );
        refetch();
      } catch (error) {
        console.error('Error deleting appointments:', error);
        showErrorSnackbar('Failed to delete appointment(s). Please try again.');
      }
    });
  };

  const handleStatusUpdate = async (appointmentId: string, status: string) => {
    try {
      const result = await updateAppointment({
        variables: {
          appointmentId,
          status,
        },
      });
      
      if (handleMutationResponse(result, `Appointment marked as ${getStatusLabel(status)}!`, 'updateAppointment')) {
        refetch();
      }
    } catch (error) {
      console.error('Error updating appointment status:', error);
      showErrorSnackbar('Failed to update appointment status. Please try again.');
    }
  };

  // Status update modal handlers
  const handleOpenStatusModal = (group: AppointmentGroup) => {    
    setStatusUpdateModal({
      open: true,
      group,
    });
  };

  const handleCloseStatusModal = () => {
    setStatusUpdateModal({
      open: false,
      group: null,
    });
  };

  const handleGroupStatusUpdate = async (status: string) => {
    if (!statusUpdateModal.group) return;

    try {
      // Update appointments sequentially so the backend's group_all_show check
      // sees each committed status before the next mutation runs. Promise.all
      // would create a race where every mutation reads the others as still
      // "scheduled" and the review request email is never sent.
      for (const appointment of statusUpdateModal.group.appointments) {
        await updateAppointment({
          variables: {
            appointmentId: appointment.id,
            status,
          },
        });
      }
      
      showSuccessSnackbar(`All appointments marked as ${getStatusLabel(status)}!`);
      refetch();
      handleCloseStatusModal();
    } catch (error) {
      console.error('Error updating appointment statuses:', error);
      showErrorSnackbar('Failed to update appointment statuses. Please try again.');
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const renderAppointmentGroupCard = (group: AppointmentGroup) => {
    const timezone = getStoreTimezone();
    
    const getGroupStatusColor = () => {
      switch (group.groupStatus) {
        case 'all_scheduled':
          return getStatusColor('scheduled');
        case 'all_completed':
          return getStatusColor('show');
        case 'all_no_show':
          return getStatusColor('no_show');
        default:
          return '#757575';
      }
    };

    const getGroupStatusLabel = () => {
      switch (group.groupStatus) {
        case 'all_scheduled':
          return 'Scheduled';
        case 'all_completed':
          return 'Completed';
        case 'all_no_show':
          return 'No Show';
        default:
          return 'Unknown';
      }
    };

    const formatTimeRangeForGroup = () => {
      if (group.hasMultipleAppointments) {
        return formatTimeRange(group.earliestTime, group.latestTime, timezone);
      }
      return formatTime(group.earliestTime, timezone);
    };

    const statusColor = getGroupStatusColor();

    const getPaymentChip = () => {
      const statuses: (string | null)[] = group.appointments.map((apt: any) => apt.paymentStatus ?? null);
      if (statuses.some((s) => s === 'succeeded')) {
        return <Chip label="Paid" size="small" color="success" sx={{ borderRadius: 1.5, fontWeight: 600 }} />;
      }
      if (statuses.some((s) => s === 'refunded')) {
        return <Chip label="Refunded" size="small" color="default" sx={{ borderRadius: 1.5, fontWeight: 600 }} />;
      }
      if (statuses.some((s) => s === 'canceled')) {
        return <Chip label="Payment Canceled" size="small" color="error" sx={{ borderRadius: 1.5, fontWeight: 600 }} />;
      }
      if (statuses.some((s) => s && s !== 'succeeded' && s !== 'canceled' && s !== 'refunded')) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label="Payment Pending" size="small" color="warning" sx={{ borderRadius: 1.5, fontWeight: 600 }} />
            <Tooltip title="Sync payment status">
              <IconButton
                size="small"
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await syncPaymentStatus({ variables: { appointmentId: group.appointments[0].id } });
                    refetch();
                  } catch {
                    showErrorSnackbar('Failed to sync payment status.');
                  }
                }}
              >
                <Refresh sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
      const chargesEnabled = (group.appointments[0] as any)?.store?.chargesEnabled;
      if (chargesEnabled) {
        return (
          <Chip
            label="Pay in person"
            size="small"
            variant="outlined"
            sx={{ borderRadius: 1.5, fontWeight: 600, color: 'text.secondary', borderColor: 'divider' }}
          />
        );
      }
      return null;
    };

    return (
      <Grid item xs={12} sm={6} lg={4} key={group.id}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Card 
            sx={{ 
              height: '100%',
              border: group.needsAction ? `2px solid ${theme.palette.warning.main}` : `1px solid ${theme.palette.divider}`,
              borderLeft: `4px solid ${statusColor}`,
              borderRadius: 2,
              boxShadow: theme.shadows[1],
              bgcolor: 'background.paper',
              transition: 'all 0.3s ease-in-out',
              position: 'relative',
              '&:hover': {
                boxShadow: theme.shadows[4],
                transform: 'translateY(-2px)',
              }
            }}
          >
            {/* Multiple appointments badge */}
            {group.hasMultipleAppointments && (
              <Box sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 1,
              }}>
                <Chip
                  label={`${group.appointments.length} appointments`}
                  size="small"
                  color="primary"
                  sx={{ 
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 24,
                    borderRadius: 1.5,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    boxShadow: theme.shadows[2]
                  }}
                />
              </Box>
            )}

            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0, pr: group.hasMultipleAppointments ? 2 : 0 }}>
                  <Tooltip
                    title={!group.appointments[0]?.client ? 'This person asked us to remove their data' : ''}
                    placement="bottom-start"
                    arrow
                  >
                    <Typography variant="h6" component="h2" sx={{ mb: 1, fontWeight: 600, display: 'inline-block' }}>
                      {group.clientName}
                    </Typography>
                  </Tooltip>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip
                      label={getGroupStatusLabel()}
                      size="small"
                      sx={{ 
                        backgroundColor: statusColor, 
                        color: 'white',
                        borderRadius: 1.5,
                        fontWeight: 600
                      }}
                    />
                    {group.needsAction && (
                      <Chip
                        label="Needs Action"
                        size="small"
                        color="warning"
                        icon={<Warning />}
                        sx={{ borderRadius: 1.5, fontWeight: 600 }}
                      />
                    )}
                    {getPaymentChip()}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, mt: group.hasMultipleAppointments ? 4 : 0, flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenStatusModal(group)}
                    sx={{ 
                      bgcolor: 'action.hover',
                      '&:hover': { 
                        bgcolor: group.appointments.some(apt => apt.status === 'scheduled' || apt.status === 'show') ? 'success.light' : 'warning.light',
                        color: group.appointments.some(apt => apt.status === 'scheduled' || apt.status === 'show') ? 'success.contrastText' : 'warning.contrastText'
                      }
                    }}
                  >
                    <EventAvailable fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(group.appointments.length === 1 ? group.appointments[0] : group)}
                    sx={{ 
                      bgcolor: 'action.hover',
                      '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' }
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  {(() => {
                    const isPaid = group.appointments.some((apt: any) => apt.paymentStatus === 'succeeded');
                    return (
                      <>
                        <Tooltip title={isPaid ? 'Cannot delete a paid appointment' : ''}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteGroup(group)}
                              disabled={isPaid}
                              sx={{ 
                                bgcolor: 'action.hover',
                                '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' },
                                '&:disabled': { bgcolor: 'action.disabledBackground' }
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {isPaid && (
                          <Tooltip title="Issue a refund">
                            <IconButton
                              size="small"
                              onClick={() => {
                                confirmDelete('this payment (full refund will be issued)', async () => {
                                  try {
                                    const result = await issueRefund({
                                      variables: { appointmentId: group.appointments[0].id },
                                    });
                                    if (handleMutationResponse(result, 'Refund issued successfully!', 'issueRefund')) {
                                      refetch();
                                    }
                                  } catch (error) {
                                    showErrorSnackbar('Failed to issue refund. Please try again.');
                                  }
                                });
                              }}
                              sx={{
                                bgcolor: 'action.hover',
                                '&:hover': { bgcolor: 'warning.light', color: 'warning.contrastText' },
                              }}
                            >
                              <Undo fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    );
                  })()}
                </Box>
              </Box>

              {/* Group Details */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 1.5,
                p: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1.5,
                bgcolor: 'background.default',
                mb: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ 
                    p: 0.75, 
                    borderRadius: 1, 
                    bgcolor: theme.palette.primary.main + '20',
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <Schedule fontSize="small" />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatDateWithDay(group.earliestTime, timezone)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                      {formatTimeRangeForGroup()}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ 
                    p: 0.75, 
                    borderRadius: 1, 
                    bgcolor: theme.palette.secondary.main + '20',
                    color: 'secondary.main',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <Person fontSize="small" />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {group.staffName}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ 
                    p: 0.75, 
                    borderRadius: 1, 
                    bgcolor: theme.palette.success.main + '20',
                    color: 'success.main',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <RoomService fontSize="small" />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatGBP(group.totalPrice)} • {group.totalDuration} min
                  </Typography>
                </Box>
              </Box>

              {/* Services List */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                  Services:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {group.appointments.map((appointment, index) => (
                    <Box key={appointment.id} sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      py: 0.5,
                      px: 1,
                      borderRadius: 1,
                      bgcolor: index % 2 === 0 ? 'transparent' : 'action.hover'
                    }}>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                        {appointment.service?.name || 'Unknown Service'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                          {formatTime(appointment.scheduledDate, timezone)}
                        </Typography>
                        <Chip
                          label={getStatusLabel(appointment.status)}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(appointment.status),
                            color: 'white',
                            borderRadius: 1,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 18,
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Contact Information */}
              {(group.clientPhone || group.clientEmail) && (
                <Box sx={{ mb: 2 }}>
                  {group.clientPhone && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                      📞 {group.clientPhone}
                    </Typography>
                  )}
                  {group.clientEmail && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      ✉️ {group.clientEmail}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Client Comment */}
              {group.appointments[0]?.clientComment && (
                <Box sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: 'action.hover',
                  borderLeft: `3px solid ${theme.palette.info.main}`
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', mb: 0.5 }}>
                    Client note:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {group.appointments[0].clientComment}
                  </Typography>
                </Box>
              )}

              {/* Action buttons for scheduled appointments that are past due */}
              {group.needsAction && group.appointments.some(apt => apt.status === 'scheduled' && getAppointmentStatus(apt).isPast) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                    Mark appointments:
                  </Typography>
                  {group.appointments
                    .filter(apt => apt.status === 'scheduled' && getAppointmentStatus(apt).isPast)
                    .map(appointment => (
                      <Box key={appointment.id} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', minWidth: 120 }}>
                          {appointment.service?.name}:
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          color="success"
                          startIcon={<CheckCircle />}
                          onClick={() => handleStatusUpdate(appointment.id, 'show')}
                          sx={{ 
                            flex: 1,
                            borderRadius: 1.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            py: 0.5
                          }}
                        >
                          Show
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          color="error"
                          startIcon={<Cancel />}
                          onClick={() => handleStatusUpdate(appointment.id, 'no_show')}
                          sx={{ 
                            flex: 1,
                            borderRadius: 1.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            py: 0.5
                          }}
                        >
                          No Show
                        </Button>
                      </Box>
                    ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </Grid>
    );
  };

  if (appointmentsLoading) {
    return <LoadingSpinner message="Loading appointments..." />;
  }

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      maxWidth: '1200px', 
      mx: 'auto',
      minHeight: '100vh',
      bgcolor: 'background.default'
    }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 0 },
          mb: 3,
          pb: 2,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight={700} sx={{ mb: 0.5 }}>
              Appointments
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
              Manage your barbershop appointments and bookings
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              boxShadow: theme.shadows[2],
              '&:hover': {
                boxShadow: theme.shadows[4],
              }
            }}
          >
            New Appointment
          </Button>
        </Box>

        {/* Tabs Section */}
        <Paper sx={{ 
          borderRadius: 2,
          boxShadow: theme.shadows[1],
          bgcolor: 'background.paper',
          mb: 3,
          overflow: 'hidden'
        }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              aria-label="appointment tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                px: { xs: 0.5, sm: 2 },
                '& .MuiTab-root': {
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  minHeight: { xs: 48, sm: 64 },
                  px: { xs: 1.5, sm: 2 },
                }
              }}
            >
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Badge badgeContent={needsAction.length} color="error">
                      <Box>Needs Action</Box>
                    </Badge>
                  </Box>
                }
              />
              <Tab label={`Upcoming (${upcoming.length})`} />
              <Tab label={`Passed${currentTab === 2 ? ` (${passedTotalCount})` : ''}`} />
            </Tabs>
          </Box>
        </Paper>

        {/* Needs Action Section */}
        {currentTab === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {needsAction.length === 0 ? (
              <Paper sx={{ 
                p: { xs: 3, md: 4 }, 
                textAlign: 'center',
                borderRadius: 2,
                boxShadow: theme.shadows[1],
                bgcolor: 'background.paper'
              }}>
                <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  All caught up!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No appointments need your attention right now.
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {needsAction.map(renderAppointmentGroupCard)}
              </Grid>
            )}
          </motion.div>
        )}

        {/* Upcoming Appointments */}
        {currentTab === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {upcoming.length === 0 ? (
              <Paper sx={{ 
                p: { xs: 3, md: 4 }, 
                textAlign: 'center',
                borderRadius: 2,
                boxShadow: theme.shadows[1],
                bgcolor: 'background.paper'
              }}>
                <CalendarMonth sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  No upcoming appointments
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Start by creating your first appointment
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => handleOpenDialog()}
                  sx={{ 
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none'
                  }}
                >
                  Create Appointment
                </Button>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {upcoming.map(renderAppointmentGroupCard)}
              </Grid>
            )}
          </motion.div>
        )}

        {/* Passed Appointments — server-paginated & filterable */}
        {currentTab === 2 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {/* Filter bar */}
            <Paper
              sx={{
                p: { xs: 2, md: 2.5 },
                mb: 3,
                borderRadius: 2,
                boxShadow: theme.shadows[1],
                bgcolor: 'background.paper',
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', md: 'center' }}
              >
                <TextField
                  type="date"
                  label="From"
                  size="small"
                  value={passedFilters.startDate}
                  onChange={(e) => setPassedFilters({ startDate: e.target.value }, { resetPage: true })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 160 }}
                />
                <TextField
                  type="date"
                  label="To"
                  size="small"
                  value={passedFilters.endDate}
                  onChange={(e) => setPassedFilters({ endDate: e.target.value }, { resetPage: true })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 160 }}
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="passed-status-label">Status</InputLabel>
                  <Select
                    labelId="passed-status-label"
                    label="Status"
                    value={passedFilters.status}
                    onChange={(e) => setPassedFilters({ status: e.target.value as typeof passedFilters.status }, { resetPage: true })}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="show">Completed</MenuItem>
                    <MenuItem value="no_show">No-show</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="passed-staff-label">Staff</InputLabel>
                  <Select
                    labelId="passed-staff-label"
                    label="Staff"
                    value={passedFilters.staffId}
                    onChange={(e) => setPassedFilters({ staffId: e.target.value }, { resetPage: true })}
                  >
                    <MenuItem value="all">All staff</MenuItem>
                    {staff.map((s: any) => (
                      <MenuItem key={s.id} value={s.id}>{s.fullName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ flexGrow: 1 }} />
                <MuiLink
                  component="button"
                  type="button"
                  underline="hover"
                  sx={{ fontSize: '0.875rem', color: 'text.secondary' }}
                  onClick={() => setPassedFilters({
                    startDate: ninetyDaysAgoISO(),
                    endDate: todayISO(),
                    status: 'all',
                    staffId: 'all',
                  }, { resetPage: true })}
                >
                  Reset filters
                </MuiLink>
              </Stack>
            </Paper>

            {passedLoading ? (
              <LoadingSpinner message="Loading appointments..." />
            ) : passedGroups.length === 0 ? (
              <Paper sx={{
                p: { xs: 3, md: 4 },
                textAlign: 'center',
                borderRadius: 2,
                boxShadow: theme.shadows[1],
                bgcolor: 'background.paper'
              }}>
                <Schedule sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  No past appointments
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {passedTotalCount === 0
                    ? 'Past appointments will appear here once they are completed or marked as no-show.'
                    : 'No appointments match the current filters. Try widening the date range or resetting filters.'}
                </Typography>
              </Paper>
            ) : (
              <>
                <Grid container spacing={3}>
                  {passedGroups.map(renderAppointmentGroupCard)}
                </Grid>
                {passedTotalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination
                      count={passedTotalPages}
                      page={passedFilters.page}
                      onChange={(_, value) => setPassedFilters({ page: value })}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}
              </>
            )}
          </motion.div>
        )}

        {/* Floating Action Button */}
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
          }}
          onClick={() => handleOpenDialog()}
        >
          <Add />
        </Fab>
      </motion.div>

      {/* Appointment Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        fullScreen={window.innerWidth < theme.breakpoints.values.sm}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            boxShadow: theme.shadows[8],
            maxHeight: { xs: '100vh', sm: '90vh' }
          }
        }}
      >
        <DialogTitle sx={{
          pb: 0,
          pt: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 4 }
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                {editingAppointment ? 
                  (editingAppointment.appointments ? 
                    `Edit Service in Group` : 
                    'Edit Appointment'
                  ) : 
                  'New Appointment'
                }
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {editingAppointment ? 
                  (editingAppointment.appointments ? 
                    `Update details for the selected service in ${editingAppointment.clientName}'s appointment group` : 
                    'Update appointment details'
                  ) : 
                  'Fill in the details for your new appointment'
                }
              </Typography>
            </Box>
            <IconButton 
              onClick={handleCloseDialog}
              sx={{ 
                borderRadius: 2,
                p: 1,
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ px: 4, py: 3 }}>
          {/* Group Info Banner */}
          {editingAppointment && editingAppointment.appointments && (
            <Paper sx={{ 
              p: 3, 
              mb: 4, 
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 2, 
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <CalendarMonth />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Group Appointment Editing
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select a service below to edit its details individually
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Services Selection Section (Top for Group Editing) */}
          {editingAppointment && editingAppointment.appointments && (
            <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default', mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
                Select Service to Edit
              </Typography>
              <Grid container spacing={2}>
                {editingAppointment.appointments.map((appointment: any, index: number) => (
                  <Grid item xs={12} sm={6} md={4} key={appointment.id}>
                    <Card 
                      sx={{ 
                        p: 2, 
                        cursor: 'pointer',
                        position: 'relative',
                        border: selectedServiceIndex === index ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                        bgcolor: selectedServiceIndex === index ? 'action.selected' : 'background.paper',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          boxShadow: theme.shadows[2],
                          transform: 'translateY(-1px)'
                        }
                      }}
                      onClick={() => handleServiceSelect(index)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <RoomService sx={{ 
                          fontSize: 20, 
                          color: selectedServiceIndex === index ? 'primary.main' : 'text.secondary' 
                        }} />
                        <Typography variant="subtitle2" sx={{ 
                          fontWeight: 600,
                          color: selectedServiceIndex === index ? 'primary.main' : 'text.primary'
                        }}>
                          {appointment.service?.name || 'Unknown Service'}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {formatTime(appointment.scheduledDate, getStoreTimezone())}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatGBP(appointment.service?.price || 0)} • {appointment.service?.duration || 0} min
                      </Typography>
                      {selectedServiceIndex === index && (
                        <Box sx={{ 
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          px: 1,
                          py: 0.25,
                          bgcolor: 'primary.main', 
                          color: 'primary.contrastText',
                          borderRadius: 1,
                          boxShadow: theme.shadows[1]
                        }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>
                            EDITING
                          </Typography>
                        </Box>
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          <Grid container spacing={4}>
            {/* Left Column - Main Details */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
                  {editingAppointment?.appointments ? 'Service Details' : 'Appointment Details'}
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                    fullWidth
                    label="Staff Member"
                    select
                    value={formData.staffId}
                    onChange={(e) => handleStaffChange(e.target.value)}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                    InputProps={{
                      endAdornment: formData.staffId ? (
                        <IconButton
                          size="small"
                          onClick={handleClearStaff}
                          sx={{ mr: 1 }}
                        >
                          <Close />
                        </IconButton>
                      ) : null,
                    }}
                  >
                    {filteredStaff.length === 0 ? (
                      <MenuItem value="" disabled>
                        {formData.serviceId ? 'No staff available for this service' : 'No staff available'}
                      </MenuItem>
                    ) : (
                      filteredStaff.map((staffMember: any) => (
                        <MenuItem key={staffMember.id} value={staffMember.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Person sx={{ fontSize: 20, color: 'text.secondary' }} />
                            <Typography>{staffMember.fullName}</Typography>
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </TextField>

                  <TextField
                    fullWidth
                    label="Service"
                    select
                    value={formData.serviceId}
                    onChange={(e) => handleServiceChange(e.target.value)}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                    InputProps={{
                      endAdornment: formData.serviceId ? (
                        <IconButton
                          size="small"
                          onClick={handleClearService}
                          sx={{ mr: 1 }}
                        >
                          <Close />
                        </IconButton>
                      ) : null,
                    }}
                  >
                    {filteredServices.length === 0 ? (
                      <MenuItem value="" disabled>
                        {formData.staffId ? 'No services available for this staff member' : 'No services available'}
                      </MenuItem>
                    ) : (
                      filteredServices.map((service: any) => (
                        <MenuItem key={service.id} value={service.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <RoomService sx={{ fontSize: 20, color: 'text.secondary' }} />
                            <Box>
                              <Typography>{service.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatGBP(service.price)} • {service.duration} min
                              </Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </TextField>

                  {/* Enhanced Date Picker with Availability */}
                  <AvailabilityDatePicker
                    storeId={storeId || ''}
                    selectedDate={formData.scheduledDate}
                    selectedStaffId={formData.staffId}
                    onDateSelect={(date: string) => setFormData({ ...formData, scheduledDate: date, selectedTime: '' })}
                  />

                  {/* Time Slot Picker */}
                  <TimeSlotPicker
                    storeId={storeId || ''}
                    selectedStaffId={formData.staffId}
                    selectedService={services.find((s: any) => s.id === formData.serviceId) || null}
                    selectedDate={formData.scheduledDate}
                    selectedTime={formData.selectedTime}
                    onTimeSelect={(time) => setFormData({ ...formData, selectedTime: time })}
                    disabled={!formData.staffId || !formData.serviceId || !formData.scheduledDate}
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Right Column - Client Details */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
                  Client Information
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                    fullWidth
                    label="Client Name"
                    value={formData.clientName}
                    onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Client Phone"
                    value={formData.clientPhone}
                    onChange={(e) => setFormData({ ...formData, clientPhone: e.target.value })}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Client Email"
                    type="email"
                    value={formData.clientEmail}
                    onChange={(e) => setFormData({ ...formData, clientEmail: e.target.value })}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Comments"
                    multiline
                    rows={4}
                    value={formData.clientComment}
                    onChange={(e) => setFormData({ ...formData, clientComment: e.target.value })}
                    placeholder="Any special instructions or notes..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ 
          px: 4, 
          py: 3, 
          gap: 2, 
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.default'
        }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            size="large"
            sx={{ 
              borderRadius: 2,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 120
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            size="large"
            sx={{ 
              borderRadius: 2,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 120,
              boxShadow: theme.shadows[3],
              '&:hover': {
                boxShadow: theme.shadows[6]
              }
            }}
          >
            {editingAppointment ? 
              (editingAppointment.appointments ? 
                'Update Service' : 
                'Update Appointment'
              ) : 
              'Create Appointment'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Modal */}
      <Dialog
        open={statusUpdateModal.open}
        onClose={handleCloseStatusModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 2, sm: 3 },
            boxShadow: theme.shadows[8],
            m: { xs: 1.5, sm: 4 },
            maxHeight: { xs: 'calc(100vh - 24px)', sm: 'calc(100vh - 64px)' },
          }
        }}
      >
        <DialogTitle sx={{
          pb: 1.5,
          pt: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 4 }
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                Update Appointment Status
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {statusUpdateModal.group ?
                  `Update status for all appointments in ${statusUpdateModal.group.clientName}'s group` :
                  'Select a status for the appointments'
                }
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseStatusModal}
              size="small"
              sx={{
                borderRadius: 2,
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: { xs: 2, sm: 4 }, py: { xs: 2, sm: 3 } }}>
          {statusUpdateModal.group && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
                Appointments to Update:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {statusUpdateModal.group.appointments
                  .map((appointment, index) => (
                    <Box key={appointment.id} sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      py: 1,
                      px: 1.5,
                      borderRadius: 1,
                      bgcolor: index % 2 === 0 ? 'action.hover' : 'transparent'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <RoomService sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                          {appointment.service?.name || 'Unknown Service'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {formatTime(appointment.scheduledDate, getStoreTimezone())}
                        </Typography>
                        <Chip
                          label={getStatusLabel(appointment.status)}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(appointment.status),
                            color: 'white',
                            borderRadius: 1,
                            fontWeight: 500,
                            height: 22,
                            fontSize: '0.7rem',
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
              </Box>
            </Box>
          )}

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
            Select New Status:
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={<CheckCircle />}
              onClick={() => handleGroupStatusUpdate('show')}
              sx={{
                borderRadius: 2,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                boxShadow: theme.shadows[2],
                '&:hover': {
                  boxShadow: theme.shadows[4]
                }
              }}
            >
              Mark All as Completed (Show)
            </Button>

            <Button
              variant="contained"
              color="error"
              size="large"
              startIcon={<Cancel />}
              onClick={() => handleGroupStatusUpdate('no_show')}
              sx={{
                borderRadius: 2,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                boxShadow: theme.shadows[2],
                '&:hover': {
                  boxShadow: theme.shadows[4]
                }
              }}
            >
              Mark All as No Show
            </Button>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          px: { xs: 2, sm: 4 },
          py: { xs: 2, sm: 3 },
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.default'
        }}>
          <Button
            onClick={handleCloseStatusModal}
            variant="outlined"
            fullWidth
            sx={{
              borderRadius: 2,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Confirmation Modal */}
      <ConfirmationModal
        open={confirmationState.open}
        onClose={closeConfirmation}
        onConfirm={handleConfirm}
        title={confirmationState.options.title}
        message={confirmationState.options.message}
        confirmText={confirmationState.options.confirmText}
        cancelText={confirmationState.options.cancelText}
        variant={confirmationState.options.variant}
        confirmButtonColor={confirmationState.options.confirmButtonColor}
      />

      {/* Custom Alert Modal */}
      <AlertModal
        open={alertState.open}
        onClose={closeAlert}
        title={alertState.options.title}
        message={alertState.options.message}
        confirmText={alertState.options.confirmText}
        variant={alertState.options.variant}
      />

      {/* Custom Snackbar */}
      <CustomSnackbar
        snackbar={{
          open: snackbarState.open,
          message: snackbarState.message,
          severity: snackbarState.severity,
          title: snackbarState.title,
          autoHideDuration: snackbarState.autoHideDuration,
        }}
        onClose={closeSnackbar}
      />
    </Box>
  );
};

export default Appointments;
